import React, { useState, useEffect } from 'react';
import {
  Film, Tv, Music, Trophy, Play, AlertTriangle, AlertCircle, Upload,
  CheckCircle2, Target, ArrowRight, CalendarDays, Database
} from 'lucide-react';
import DashboardCard from '@/components/ui/DashboardCard';
import { dashboardService } from '@/services/dashboardService';
import { ContentManagerDashboardData } from '@/types';
import { useNavigate } from 'react-router-dom';

const ContentManagerDashboard: React.FC = () => {
  const [data, setData] = useState<ContentManagerDashboardData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const navigate = useNavigate();

  useEffect(() => {
    const loadDashboardData = async () => {
      try {
        setLoading(true);
        const dashboardData = await dashboardService.getContentManagerDashboard();
        setData(dashboardData);
      } catch (error) {
        setError(error instanceof Error ? error.message : 'Error loading dashboard data');
      } finally {
        setLoading(false);
      }
    };

    loadDashboardData();
  }, []);

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 p-6">
        <div className="max-w-7xl mx-auto">
          <h1 className="text-3xl font-bold text-gray-900 mb-8">Panel de Gestión de Contenido</h1>
          <div className="flex items-center justify-center h-64">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 p-6">
        <div className="max-w-7xl mx-auto">
          <h1 className="text-3xl font-bold text-gray-900 mb-8">Panel de Gestión de Contenido</h1>
          <div className="bg-red-50 border border-red-200 rounded-md p-4">
            <div className="flex">
              <AlertCircle className="h-5 w-5 text-red-400" />
              <div className="ml-3">
                <h3 className="text-sm font-medium text-red-800">Error</h3>
                <p className="text-sm text-red-700 mt-1">{error}</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!data) return null;

  const formatBytes = (bytes: number) => {
    if (!bytes) return '0 B';
    const units = ['B', 'KB', 'MB', 'GB', 'TB'];
    const index = Math.min(Math.floor(Math.log(bytes) / Math.log(1024)), units.length - 1);
    return `${(bytes / 1024 ** index).toFixed(index > 2 ? 1 : 0)} ${units[index]}`;
  };

  const contentTypes = [
    { key: 'movies', label: 'Películas', icon: Film, color: 'blue', onClick: () => navigate('/content/movies') },
    { key: 'series', label: 'Series', icon: Tv, color: 'green', onClick: () => navigate('/content/tv-shows') },
    { key: 'episodes', label: 'Episodios', icon: Play, color: 'purple' },
    { key: 'concerts', label: 'Conciertos', icon: Music, color: 'pink', onClick: () => navigate('/content/concerts') },
    { key: 'sports', label: 'Deportes', icon: Trophy, color: 'yellow', onClick: () => navigate('/content/sports') },
    { key: 'documentaries', label: 'Documentales', icon: Film, color: 'indigo', onClick: () => navigate('/content/documentaries') },
    { key: 'tvChannels', label: 'Canales TV', icon: Tv, color: 'red', onClick: () => navigate('/content/tv-channels') },
  ];

  const issueTypes = [
    { key: 'TV_CHANNEL', label: 'Canales TV', color: 'red' },
    { key: 'MOVIE', label: 'Películas', color: 'blue' },
    { key: 'DOCUMENTARY', label: 'Documentales', color: 'indigo' },
    { key: 'SPORT', label: 'Deportes', color: 'yellow' },
    { key: 'CONCERT', label: 'Conciertos', color: 'pink' },
    { key: 'EPISODE', label: 'Episodios', color: 'purple' },
    { key: 'TRAILER', label: 'Trailers', color: 'gray' },
  ];

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="max-w-7xl mx-auto">
        <h1 className="text-3xl font-bold text-gray-900 mb-8">Panel de Gestión de Contenido</h1>

        <section className="mb-8 bg-gradient-to-br from-indigo-950 via-indigo-900 to-violet-900 rounded-xl shadow-lg overflow-hidden text-white">
          <div className="p-6">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-6">
              <div>
                <div className="flex items-center gap-2 text-indigo-200 text-sm font-medium mb-1">
                  <CalendarDays className="h-4 w-4" />
                  Mi productividad
                </div>
                <h2 className="text-2xl font-bold">Tu trabajo de contenido</h2>
                <p className="text-indigo-200 text-sm mt-1">Resumen personal de cargas, contenido completado y metas activas.</p>
              </div>
              <button
                onClick={() => navigate('/reports/content-productivity')}
                className="inline-flex items-center justify-center gap-2 rounded-md bg-white/10 hover:bg-white/20 border border-white/20 px-4 py-2 text-sm font-medium transition-colors"
              >
                Ver reporte completo <ArrowRight className="h-4 w-4" />
              </button>
            </div>

            <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-6">
              <div className="rounded-lg bg-white/10 border border-white/10 p-4">
                <Upload className="h-5 w-5 text-sky-300 mb-3" />
                <p className="text-2xl font-bold">{data.personalProductivity.thisWeek.filesUploaded.toLocaleString()}</p>
                <p className="text-xs text-indigo-200 mt-1">Archivos esta semana</p>
              </div>
              <div className="rounded-lg bg-white/10 border border-white/10 p-4">
                <CheckCircle2 className="h-5 w-5 text-emerald-300 mb-3" />
                <p className="text-2xl font-bold">{data.personalProductivity.thisMonth.contentCompleted.toLocaleString()}</p>
                <p className="text-xs text-indigo-200 mt-1">Completados este mes</p>
              </div>
              <div className="rounded-lg bg-white/10 border border-white/10 p-4">
                <Database className="h-5 w-5 text-violet-300 mb-3" />
                <p className="text-2xl font-bold">{formatBytes(data.personalProductivity.thisMonth.uploadedBytes)}</p>
                <p className="text-xs text-indigo-200 mt-1">Cargados este mes</p>
              </div>
              <div className="rounded-lg bg-white/10 border border-white/10 p-4">
                <CheckCircle2 className="h-5 w-5 text-amber-300 mb-3" />
                <p className="text-2xl font-bold">{Math.round(data.personalProductivity.monthValidationRate)}%</p>
                <p className="text-xs text-indigo-200 mt-1">Archivos válidos</p>
              </div>
            </div>

            <div className="grid lg:grid-cols-[240px_1fr] gap-4">
              <div className="grid grid-cols-2 lg:grid-cols-1 gap-3">
                <div className="rounded-lg bg-black/15 p-3 flex items-center justify-between">
                  <div>
                    <p className="text-xs text-indigo-200">Películas completadas</p>
                    <p className="text-xl font-bold mt-1">{data.personalProductivity.monthMoviesCompleted.toLocaleString()}</p>
                  </div>
                  <Film className="h-6 w-6 text-sky-300" />
                </div>
                <div className="rounded-lg bg-black/15 p-3 flex items-center justify-between">
                  <div>
                    <p className="text-xs text-indigo-200">Episodios completados</p>
                    <p className="text-xl font-bold mt-1">{data.personalProductivity.monthEpisodesCompleted.toLocaleString()}</p>
                  </div>
                  <Play className="h-6 w-6 text-violet-300" />
                </div>
              </div>

              <div className="rounded-lg bg-black/15 p-4">
                <div className="flex items-center justify-between mb-3">
                  <h3 className="font-semibold flex items-center gap-2">
                    <Target className="h-5 w-5 text-amber-300" /> Metas activas
                  </h3>
                  <span className="text-xs text-indigo-200">{data.personalProductivity.activeGoals.length} asignadas</span>
                </div>
                {data.personalProductivity.activeGoals.length ? (
                  <div className="space-y-3">
                    {data.personalProductivity.activeGoals.slice(0, 3).map((goal) => (
                      <div key={goal.id}>
                        <div className="flex justify-between gap-3 text-sm mb-1.5">
                          <span className="truncate">
                            {goal.recurrenceType === 'WEEKLY' ? 'Semanal' : goal.recurrenceType === 'MONTHLY' ? 'Mensual' : 'Meta única'}
                            {' · '}
                            {goal.contentType === 'ALL' ? 'Todo el contenido' : goal.contentType === 'MOVIE' ? 'Películas' : 'Episodios'}
                            {' · '}
                            {goal.metricType === 'FILES_UPLOADED' ? 'archivos' : 'completados'}
                          </span>
                          <span className="font-semibold whitespace-nowrap">
                            {goal.achievedQuantity}/{goal.targetQuantity}
                          </span>
                        </div>
                        <div className="h-2 rounded-full bg-white/15 overflow-hidden">
                          <div
                            className={`h-full rounded-full ${goal.progressPercentage >= 100 ? 'bg-emerald-400' : 'bg-amber-300'}`}
                            style={{ width: `${Math.min(100, goal.progressPercentage)}%` }}
                          />
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="h-20 flex items-center justify-center text-sm text-indigo-200">
                    No tienes metas activas para hoy.
                  </div>
                )}
              </div>
            </div>
          </div>
        </section>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-8">
          <div className="bg-white rounded-lg shadow-md p-6 border border-gray-200">
            <h2 className="text-xl font-semibold text-gray-900 mb-4 flex items-center">
              <Play className="h-6 w-6 mr-2" />
              Contenido Activo por Tipo
            </h2>
            <div className="grid grid-cols-2 gap-4">
              {contentTypes.map(({ key, label, icon: Icon, color, onClick }) => (
                <div
                  onClick={onClick}
                  key={key} className={`text-center p-4 bg-${color}-50 ${onClick && `rounded-md cursor-pointer hover:shadow-md transition-shadow`}`}>
                  <Icon className={`h-8 w-8 mx-auto mb-2 text-${color}-600`} />
                  <p className={`text-2xl font-bold text-${color}-900`}>
                    {data.contentCounts[key as keyof typeof data.contentCounts].toLocaleString()}
                  </p>
                  <p className={`text-sm text-${color}-600`}>{label}</p>
                </div>
              ))}
            </div>
          </div>

          <div className="bg-white rounded-lg shadow-md p-6 border border-gray-200">
            <h2 className="hidden bg-gray-500 bg-blue-500 bg-red-500 bg-indigo-500 bg-purple-500 bg-pink-500"/>
            <h2 className="text-xl font-semibold text-gray-900 mb-4 flex items-center">
              <AlertTriangle className="h-6 w-6 mr-2" />
              Reportes No Resueltos por Tipo
            </h2>
            <div className="space-y-3">
              {issueTypes.map(({ key, label, color }) => {
                const count = data.unresolvedIssues[key as keyof typeof data.unresolvedIssues] || 0;
                return (
                  <div key={key} className="flex items-center justify-between p-3 bg-gray-50 rounded-md">
                    <div className="flex items-center">
                      <div className={`w-4 h-4 rounded-full bg-${color}-500 mr-3`}></div>
                      <span className="font-medium text-gray-900">{label}</span>
                    </div>
                    <div className={`px-2 py-1 rounded-full text-sm font-medium ${
                      count > 0 ? 'bg-red-100 text-red-800' : 'bg-green-100 text-green-800'
                    }`}>
                      {count.toLocaleString()}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <DashboardCard
            title="Total de Reportes Pendientes"
            value={Object.values(data.unresolvedIssues).reduce((sum, count) => sum + count, 0).toLocaleString()}
            subtitle="Requieren atención inmediata"
            icon={<AlertTriangle className="h-8 w-8" />}
            className="border-red-200 bg-red-50"
          />

          <DashboardCard
            title="Total de Contenido Activo"
            value={Object.values(data.contentCounts).reduce((sum, count) => sum + count, 0).toLocaleString()}
            subtitle="Disponible en la plataforma"
            icon={<Play className="h-8 w-8" />}
            className="border-green-200 bg-green-50"
          />

          <DashboardCard
            title="Tipos de Contenido"
            value={Object.keys(data.contentCounts).length}
            subtitle="Categorías disponibles"
            icon={<Film className="h-8 w-8" />}
            className="border-blue-200 bg-blue-50"
          />

        </div>
      </div>
    </div>
  );
};

export default ContentManagerDashboard;
